import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Brain, Zap, Target, Clock, CheckCircle, XCircle, 
  ChevronRight, Trophy, Flame, ArrowRight, Sparkles,
  RotateCcw, Award, TrendingUp, AlertTriangle
} from 'lucide-react';

const STEPS = [
  { id: 'recall', title: 'Quick Recall', subtitle: 'Test your memory', icon: Brain, color: 'violet' },
  { id: 'boost', title: 'Concept Boost', subtitle: 'Learn from mistakes', icon: Sparkles, color: 'cyan' },
  { id: 'practice', title: 'Mixed Practice', subtitle: 'Interleaved questions', icon: Target, color: 'emerald' },
  { id: 'test', title: 'Mini Test', subtitle: 'Timed challenge', icon: Clock, color: 'amber' },
  { id: 'feedback', title: 'Results', subtitle: 'See your progress', icon: Trophy, color: 'pink' }
];

export default function DailyLearningLoop({ onNavigate, params }) {
  const { api, user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState({});
  const [showExplanation, setShowExplanation] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes for mini test
  const [testStarted, setTestStarted] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);

  // Fetch learning session
  const fetchSession = useCallback(async () => {
    try {
      const res = await api.post('/learning/start-session', {
        subject: params?.subject || null,
        chapter_number: params?.chapter || null
      });
      setSession(res.data);
    } catch (err) {
      console.error('Failed to start learning session:', err);
    }
    setLoading(false);
  }, [api, params]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  // Timer for mini test
  useEffect(() => {
    if (currentStep === 3 && testStarted && timeLeft > 0) {
      const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
      return () => clearInterval(timer);
    }
    if (timeLeft === 0 && currentStep === 3) {
      handleSubmitTest();
    }
  }, [currentStep, testStarted, timeLeft]);

  const handleAnswer = async (questionIndex, answer) => {
    setSelectedAnswer(answer);
    const question = getCurrentQuestions()[questionIndex];
    const correct = answer === question.correct_answer;
    setIsCorrect(correct);
    
    setAnswers(prev => ({ ...prev, [questionIndex]: answer }));
    
    // Auto-clear selection after 1.5 seconds to allow next question
    setTimeout(() => {
      setSelectedAnswer(null);
      setIsCorrect(null);
    }, 1500);
  };

  const handleSubmitTest = async () => {
    setLoading(true);
    try {
      const res = await api.post('/learning/complete-session', {
        session_id: session?.session_id,
        answers: answers,
        time_taken: 120 - timeLeft
      });
      setResults(res.data);
      setCurrentStep(4);
    } catch (err) {
      console.error('Failed to submit session:', err);
    }
    setLoading(false);
  };

  const getCurrentQuestions = () => {
    if (!session) return [];
    switch (currentStep) {
      case 0: return session.recall_questions || [];
      case 2: return session.practice_questions || [];
      case 3: return session.test_questions || [];
      default: return [];
    }
  };

  const getConceptBoost = () => {
    return session?.concept_boost || [];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-violet-600/20 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Brain className="w-8 h-8 text-violet-400" />
          </div>
          <p className="text-slate-400">Preparing your learning session...</p>
        </div>
      </div>
    );
  }

  const step = STEPS[currentStep];
  const StepIcon = step.icon;

  return (
    <div className="min-h-screen bg-[#0f172a] pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0f172a]/95 backdrop-blur-sm border-b border-slate-800/50 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <button onClick={() => onNavigate('dashboard')} className="text-slate-400 hover:text-white">
            <XCircle className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            {STEPS.map((s, i) => (
              <div key={s.id} className={`w-2 h-2 rounded-full transition-all ${
                i < currentStep ? 'bg-emerald-500' :
                i === currentStep ? 'bg-violet-500 w-6' :
                'bg-slate-700'
              }`} />
            ))}
          </div>
          <div className="flex items-center gap-1 text-amber-400">
            <Flame className="w-4 h-4" />
            <span className="text-sm font-semibold">{user?.streak || 0}</span>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-6">
        {/* Step Header */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className={`w-16 h-16 rounded-2xl bg-${step.color}-500/20 flex items-center justify-center mx-auto mb-4`}>
            <StepIcon className={`w-8 h-8 text-${step.color}-400`} />
          </div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">{step.title}</h1>
          <p className="text-slate-400 text-sm mt-1">{step.subtitle}</p>
        </motion.div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          {/* Step 1: Quick Recall */}
          {currentStep === 0 && (
            <RecallStep 
              questions={getCurrentQuestions()}
              answers={answers}
              onAnswer={handleAnswer}
              selectedAnswer={selectedAnswer}
              isCorrect={isCorrect}
              onNextStep={() => setCurrentStep(1)}
            />
          )}

          {/* Step 2: Concept Boost */}
          {currentStep === 1 && (
            <ConceptBoostStep 
              concepts={getConceptBoost()}
              onContinue={() => setCurrentStep(2)}
            />
          )}

          {/* Step 3: Mixed Practice */}
          {currentStep === 2 && (
            <PracticeStep 
              questions={getCurrentQuestions()}
              answers={answers}
              onAnswer={handleAnswer}
              selectedAnswer={selectedAnswer}
              isCorrect={isCorrect}
              onComplete={() => setCurrentStep(3)}
            />
          )}

          {/* Step 4: Mini Test */}
          {currentStep === 3 && (
            <MiniTestStep 
              questions={getCurrentQuestions()}
              answers={answers}
              onAnswer={handleAnswer}
              timeLeft={timeLeft}
              testStarted={testStarted}
              onStart={() => setTestStarted(true)}
              onSubmit={handleSubmitTest}
            />
          )}

          {/* Step 5: Results */}
          {currentStep === 4 && results && (
            <ResultsStep 
              results={results}
              onNavigate={onNavigate}
              onRetry={fetchSession}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// Sub-components
function RecallStep({ questions, answers, onAnswer, selectedAnswer, isCorrect, onNextStep }) {
  const currentQIndex = Object.keys(answers).length;
  const currentQ = questions[currentQIndex];
  const allAnswered = currentQIndex >= questions.length;

  if (allAnswered || !currentQ) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center py-8"
      >
        <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-white mb-2">Recall Complete!</h3>
        <p className="text-slate-400 mb-6">Let&apos;s boost your understanding</p>
        <button
          onClick={onNextStep}
          className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl px-8 py-3 font-semibold flex items-center gap-2 mx-auto"
        >
          Continue <ArrowRight className="w-5 h-5" />
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
        <p className="text-xs text-slate-500 mb-2">Question {currentQIndex + 1} of {questions.length}</p>
        <p className="text-lg text-white font-medium">{currentQ.question}</p>
      </div>

      <div className="space-y-3">
        {currentQ.options?.map((option, i) => {
          const isSelected = selectedAnswer === option;
          const isCorrectOption = option === currentQ.correct_answer;
          
          return (
            <button
              key={i}
              onClick={() => !selectedAnswer && onAnswer(currentQIndex, option)}
              disabled={selectedAnswer !== null}
              className={`w-full p-4 rounded-xl border text-left transition-all ${
                selectedAnswer === null
                  ? 'bg-slate-900/50 border-slate-800 hover:border-violet-500/50'
                  : isSelected
                    ? isCorrect
                      ? 'bg-emerald-500/20 border-emerald-500'
                      : 'bg-red-500/20 border-red-500'
                    : isCorrectOption && !isCorrect
                      ? 'bg-emerald-500/20 border-emerald-500'
                      : 'bg-slate-900/30 border-slate-800 opacity-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-white">{option}</span>
                {selectedAnswer && isSelected && (
                  isCorrect 
                    ? <CheckCircle className="w-5 h-5 text-emerald-400" />
                    : <XCircle className="w-5 h-5 text-red-400" />
                )}
                {selectedAnswer && !isSelected && isCorrectOption && !isCorrect && (
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Show Next button after answering */}
      {selectedAnswer && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="pt-4"
        >
          <p className="text-center text-sm text-slate-400 mb-3">
            {isCorrect ? '✓ Correct!' : `✗ The answer was: ${currentQ.correct_answer}`}
          </p>
        </motion.div>
      )}
    </motion.div>
  );
}

function ConceptBoostStep({ concepts, onContinue }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      {concepts.map((concept, i) => (
        <div key={i} className="bg-slate-900/50 border border-cyan-500/20 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-white font-medium mb-2">{concept.title}</h3>
              <p className="text-slate-400 text-sm">{concept.explanation}</p>
              {concept.example && (
                <div className="mt-3 bg-slate-950/50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Example:</p>
                  <p className="text-cyan-300 text-sm">{concept.example}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      <button
        onClick={onContinue}
        className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl h-14 font-semibold flex items-center justify-center gap-2 transition-all"
      >
        Continue to Practice <ArrowRight className="w-5 h-5" />
      </button>
    </motion.div>
  );
}

function PracticeStep({ questions, answers, onAnswer, selectedAnswer, isCorrect, onComplete }) {
  const answeredCount = Object.keys(answers).filter(k => parseInt(k) >= 0).length;
  const currentQ = questions.find((_, i) => answers[i] === undefined);
  const qIndex = currentQ ? questions.indexOf(currentQ) : -1;

  if (!currentQ) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center py-8"
      >
        <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-white mb-2">Practice Complete!</h3>
        <p className="text-slate-400 mb-6">Ready for your mini test?</p>
        <button
          onClick={onComplete}
          className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl px-8 py-3 font-semibold flex items-center gap-2 mx-auto"
        >
          Start Mini Test <ArrowRight className="w-5 h-5" />
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-500">Mixed topics</span>
        <span className="text-emerald-400">{answeredCount}/{questions.length}</span>
      </div>

      <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-full">
            {currentQ.topic || 'Mixed'}
          </span>
        </div>
        <p className="text-lg text-white font-medium">{currentQ.question}</p>
      </div>

      <div className="space-y-3">
        {currentQ.options?.map((option, i) => {
          const isSelected = selectedAnswer === option;
          const isCorrectOption = option === currentQ.correct_answer;
          
          return (
            <button
              key={i}
              onClick={() => !selectedAnswer && onAnswer(qIndex, option)}
              disabled={selectedAnswer !== null}
              className={`w-full p-4 rounded-xl border text-left transition-all ${
                selectedAnswer === null
                  ? 'bg-slate-900/50 border-slate-800 hover:border-emerald-500/50'
                  : isSelected
                    ? isCorrect
                      ? 'bg-emerald-500/20 border-emerald-500'
                      : 'bg-red-500/20 border-red-500'
                    : isCorrectOption && !isCorrect
                      ? 'bg-emerald-500/20 border-emerald-500'
                      : 'bg-slate-900/30 border-slate-800 opacity-50'
              }`}
            >
              <span className="text-white">{option}</span>
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}

function MiniTestStep({ questions, answers, onAnswer, timeLeft, testStarted, onStart, onSubmit }) {
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const answeredCount = Object.keys(answers).filter(k => parseInt(k) >= questions.length - 5).length;

  if (!testStarted) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center py-8"
      >
        <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
          <Clock className="w-10 h-10 text-amber-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Ready for Mini Test?</h3>
        <p className="text-slate-400 mb-2">5 questions in 2 minutes</p>
        <p className="text-sm text-slate-500 mb-6">Test your knowledge with a timed challenge</p>
        <button
          onClick={onStart}
          className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl px-8 py-4 font-semibold flex items-center gap-2 mx-auto"
        >
          Start Test <Zap className="w-5 h-5" />
        </button>
      </motion.div>
    );
  }

  const currentQ = questions.slice(-5).find((_, i) => answers[questions.length - 5 + i] === undefined);
  const qIndex = currentQ ? questions.indexOf(currentQ) : -1;

  if (!currentQ) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center py-8"
      >
        <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-white mb-4">Test Complete!</h3>
        <button
          onClick={onSubmit}
          className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl px-8 py-3 font-semibold"
        >
          See Results
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Timer */}
      <div className={`text-center p-4 rounded-xl ${
        timeLeft <= 30 ? 'bg-red-500/20 border border-red-500/30' : 'bg-amber-500/20 border border-amber-500/30'
      }`}>
        <div className={`text-3xl font-bold font-mono ${timeLeft <= 30 ? 'text-red-400' : 'text-amber-400'}`}>
          {minutes}:{seconds.toString().padStart(2, '0')}
        </div>
        <p className="text-sm text-slate-400 mt-1">{answeredCount}/5 answered</p>
      </div>

      <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
        <p className="text-lg text-white font-medium">{currentQ.question}</p>
      </div>

      <div className="space-y-3">
        {currentQ.options?.map((option, i) => (
          <button
            key={i}
            onClick={() => onAnswer(qIndex, option)}
            className="w-full p-4 rounded-xl border bg-slate-900/50 border-slate-800 hover:border-amber-500/50 text-left transition-all"
          >
            <span className="text-white">{option}</span>
          </button>
        ))}
      </div>
    </motion.div>
  );
}

function ResultsStep({ results, onNavigate, onRetry }) {
  const scorePercent = Math.round((results.score / results.total) * 100);
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="space-y-6"
    >
      {/* Score Card */}
      <div className="bg-gradient-to-br from-violet-600/20 to-cyan-600/20 border border-violet-500/30 rounded-2xl p-6 text-center">
        <div className="w-24 h-24 rounded-full bg-slate-900/50 border-4 border-violet-500 flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl font-bold text-white">{results.score}/{results.total}</span>
        </div>
        <h3 className="text-xl font-bold text-white mb-1">
          {scorePercent >= 80 ? 'Excellent!' : scorePercent >= 60 ? 'Good job!' : 'Keep practicing!'}
        </h3>
        <p className="text-slate-400">You scored {scorePercent}%</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-center">
          <TrendingUp className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{results.improvement || '+0'}%</p>
          <p className="text-xs text-slate-500">Improvement</p>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-center">
          <Flame className="w-6 h-6 text-orange-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{results.streak || 1}</p>
          <p className="text-xs text-slate-500">Day Streak</p>
        </div>
      </div>

      {/* Weak Topics */}
      {results.weak_topics && results.weak_topics.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <span className="text-amber-300 font-medium">Focus Areas</span>
          </div>
          <div className="space-y-2">
            {results.weak_topics.map((topic, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-slate-300">{topic.name}</span>
                <span className="text-amber-400">{topic.score}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* XP Earned */}
      <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Award className="w-8 h-8 text-violet-400" />
          <div>
            <p className="text-white font-medium">XP Earned</p>
            <p className="text-xs text-slate-400">Keep learning daily!</p>
          </div>
        </div>
        <span className="text-2xl font-bold text-violet-400">+{results.xp_earned || 25}</span>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={onRetry}
          className="bg-slate-800 hover:bg-slate-700 text-white rounded-xl py-3 font-medium flex items-center justify-center gap-2"
        >
          <RotateCcw className="w-4 h-4" /> Try Again
        </button>
        <button
          onClick={() => onNavigate('dashboard')}
          className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl py-3 font-medium flex items-center justify-center gap-2"
        >
          Done <CheckCircle className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}
